/**
 * Share a reading link (part 38d; cockatrice-ui `CreateLink.jsx`, its two steps; decisions 58, 63): the form (a name
 * only the writer sees, all chapters or a range, comments allowed, an end date), Create link, then the link ready with
 * Copy link and the design's warnings. Only for a paid account (DP8): any other is told the links come with the purchase.
 * The copy is the open book's chapters as the exports read them (`Workspace.shareCopy`); the rest is `Sharing.make`'s.
 * Errors are said in the dialog's status, once, beside the button that met them. PLAN.md §3, step 6: built from the
 * component library (TextField, SegmentedControl, Select, Checkbox, Banner, CopyField, Button).
 */
import { createSignal } from 'solid-js';
import type { Accessor } from 'solid-js';
import type { ChapterEntry } from '@cockatrice/storage';
import type { RelayError } from '../account/client.ts';
import type { MakeOutcome, PendingLink } from '../share/links.ts';
import { dateWords } from './account-sheet.ts';
import { shownTitle } from './chapter-list.ts';
import { el, when, whenSome } from '../ui/dom.ts';
import type { Child } from '../ui/dom.ts';
import Banner from '../ui/Banner.ts';
import Button from '../ui/Button.ts';
import Checkbox from '../ui/Checkbox.ts';
import CopyField from '../ui/CopyField.ts';
import SegmentedControl from '../ui/SegmentedControl.ts';
import Select from '../ui/Select.ts';
import TextField, { TextInput } from '../ui/TextField.ts';
import { component } from '../ui/solid.ts';
import { ModalBody, ModalButtons, ModalHead } from './modal.ts';
import type { Shell } from './shell.ts';

/** Bytes in the units the dialog says them in. */
export function sizeWords(shell: Pick<Shell, 'locale'>, bytes: number): string {
  const format = (value: number, unit: 'kilobyte' | 'megabyte'): string => new Intl.NumberFormat(shell.locale, { style: 'unit', unit, maximumFractionDigits: 1 }).format(value);
  return bytes < 1024 * 1024 ? format(Math.max(0.1, bytes / 1024), 'kilobyte') : format(bytes / (1024 * 1024), 'megabyte');
}

/** The end of local day `date` (`YYYY-MM-DD`, an `<input type="date">`'s value): the next day's start, in milliseconds; null for none. */
export function endOfDay(date: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(date);
  if (match === null) return null;
  const part = (at: number): number => Number(match[at]);
  const [year, month, day] = [part(1), part(2), part(3)];
  const that = new Date(year, month - 1, day);
  // A day the calendar doesn't have (31 June) is none.
  if (that.getFullYear() !== year || that.getMonth() !== month - 1 || that.getDate() !== day) return null;
  return new Date(year, month - 1, day + 1).getTime();
}

/** A local day as an `<input type="date">` holds it. */
export function dayOf(at: number): string {
  const date = new Date(at);
  return `${String(date.getFullYear()).padStart(4, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** Decision 63's refusals of a link in the dialog's words; any other is a failure. */
const REFUSALS: Partial<Readonly<Record<RelayError, 'share.error.purchase' | 'share.error.tooMany' | 'share.error.quota' | 'share.error.today'>>> = {
  'purchase-required': 'share.error.purchase',
  'too-many-links': 'share.error.tooMany',
  'over-quota': 'share.error.quota',
  'rate-limited': 'share.error.today',
};

/** A refusal or failure in the dialog's words. */
function makeProblem(shell: Shell, made: Exclude<MakeOutcome, { readonly kind: 'ready' }>): string {
  const { t } = shell;
  if (made.kind === 'too-large') return t('share.error.tooLarge');
  if (made.kind === 'unfinished') return t('share.error.unfinished');
  const key = made.kind === 'refused' ? REFUSALS[made.error] : undefined;
  return t(key ?? 'share.error.failed');
}

/** The dialog's head: its title (the dialog's name) and Close. */
function Head(shell: Shell, title: Accessor<string>): Child {
  // Part 11g: the modal's head (`modal.ts`).
  return ModalHead(shell, title, { closeClass: 'share-close' });
}

/** What a made link shows: its record's summary and its URL. */
interface Made {
  readonly url: string;
  readonly name: string;
  readonly summary: string;
  readonly size: number;
}

/** The ready step: the summary, the link with Copy link, the warnings, Done. */
function Ready(shell: Shell, made: Made): Child {
  const { t, ui } = shell;
  const [failed, setFailed] = createSignal(false);
  return [
    ModalBody({},
      el(
        'div',
        { class: 'sh-stack' },
        el('p', { class: 'sh-help share-summary' }, t('share.summaryFor'), ' ', el('strong', {}, made.name), ' ', made.summary),
        // The link and Copy link; "Copied" is the field's own, a copy that failed is said below.
        component(CopyField, {
          class: 'share-url',
          label: t('share.link'),
          hideLabel: true,
          copyLabel: t('share.copy'),
          value: made.url,
          copy: shell.copy,
          onCopy: (_url: string, done: boolean) => {
            setFailed(!done);
          },
          // The design's ready step: the link in focus, its start in view (the reader page's address, not the key's tail).
          inputRef: (input: HTMLInputElement) => {
            queueMicrotask(() => {
              input.focus();
              input.setSelectionRange(0, 0);
              input.scrollLeft = 0;
            });
          },
        }),
        el('p', { class: 'ac-error share-copy-status', role: 'status' }, () => (failed() ? t('share.copyFailed') : '')),
        el('ul', { class: 'sh-warnings' }, el('li', {}, t('share.warnAnyone')), el('li', {}, t('share.warnRevoke')), el('li', {}, t('share.warnCopy')), el('li', {}, t('share.warnSize', { size: sizeWords(shell, made.size) }))),
      ),
    ),
    ModalButtons(
      component(Button, {
        variant: 'primary',
        class: 'share-done',
        onClick: () => {
          ui.setDialog(null);
          shell.go('sharing');
        },
        children: t('share.done'),
      }),
    ),
  ];
}

/** The chapter range's selects: From and To over the book's chapters in order. */
function Range(shell: Shell, chapters: readonly ChapterEntry[], from: Accessor<number>, setFrom: (at: number) => void, to: Accessor<number>, setTo: (at: number) => void): Child {
  const { t } = shell;
  const options = chapters.map((chapter, index) => ({ value: String(index), label: t('share.chapterOption', { number: index + 1, title: shownTitle(shell, chapter) }) }));
  const select = (id: string, label: string, value: Accessor<number>, set: (at: number) => void): Child =>
    component(Select, {
      id,
      label,
      options,
      get value() {
        return String(value());
      },
      'aria-describedby': 'share-error',
      onChange: (picked: string) => {
        set(Number(picked));
      },
    });
  return el('div', { class: 'sh-range' }, select('share-from', t('share.from'), from, setFrom), select('share-to', t('share.to'), to, setTo));
}

/** The form step (DP8: a paid account's): the fields, Cancel and Create link. */
function Form(shell: Shell, done: (made: Made) => void): Child {
  const { t, ui, workspace, sharing } = shell;
  const chapters = workspace.listing()?.chapters ?? [];
  const [name, setName] = createSignal('');
  const [range, setRange] = createSignal<'all' | 'some'>('all');
  const [from, setFrom] = createSignal(0);
  const [to, setTo] = createSignal(Math.max(0, chapters.length - 1));
  const [comments, setComments] = createSignal(true);
  const [ends, setEnds] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal('');
  const [pending, setPending] = createSignal<PendingLink | null>(null);
  const today = dayOf(shell.now());
  const finish = (made: MakeOutcome, first: number, last: number): void => {
    if (made.kind !== 'ready') {
      setPending(made.kind === 'unfinished' ? made.pending : null);
      setError(makeProblem(shell, made));
      return;
    }
    const which = range() === 'all' ? t('share.summaryAll') : first === last ? t('share.summaryOne', { from: first + 1 }) : t('share.summaryRange', { from: first + 1, to: last + 1 });
    done({ url: made.url, name: made.record.name, size: made.record.size, summary: t('share.summary', { chapters: which, date: dateWords(shell, made.record.createdAt) }) });
  };
  const submit = async (): Promise<void> => {
    if (busy()) return;
    setError('');
    const book = workspace.book();
    if (book === null) return;
    const retry = pending();
    const [first, last] = range() === 'all' ? [0, chapters.length - 1] : [from(), to()];
    const chosen = chapters.slice(first, last + 1);
    if (retry !== null) {
      setBusy(true);
      finish(await sharing.upload(retry).finally(() => setBusy(false)), first, last);
      return;
    }
    if (first > last) {
      setError(t('share.error.range'));
      return;
    }
    const expiresAt = ends() === '' ? null : endOfDay(ends());
    if (ends() !== '' && (expiresAt === null || ends() < today)) {
      setError(t('share.error.date'));
      return;
    }
    setBusy(true);
    try {
      const shared = await workspace.shareCopy(new Set(chosen.map((chapter) => chapter.id)));
      if (shared === null || shared.chapters.length === 0) {
        setError(t('share.error.read'));
        return;
      }
      const named = name().trim() === '' ? t('share.unnamed', { date: dateWords(shell, shell.now()) }) : name().trim();
      finish(await sharing.make({ bookId: book.id, name: named, chapters: shared.chapters, comments: comments(), expiresAt, copy: shared.copy }), first, last);
    } finally {
      setBusy(false);
    }
  };
  return el(
    'form',
    {
      class: 'c-modal__form',
      noValidate: true,
      onSubmit: (event: SubmitEvent) => {
        event.preventDefault();
        void submit();
      },
    },
    ModalBody({},
      el(
        'div',
        { class: 'sh-stack' },
        el(
          'div',
          { class: 'sh-field' },
          el('label', { for: 'share-name' }, t('share.name'), ' ', el('span', { class: 'sh-help', id: 'share-name-help' }, t('share.nameHelp'))),
          component(TextField, {
            id: 'share-name',
            autocomplete: 'off',
            maxLength: 200,
            autofocus: true,
            'aria-describedby': 'share-name-help',
            get value() {
              return name();
            },
            onChange: (value: string) => {
              setName(value);
            },
          }),
        ),
        el(
          'div',
          { class: 'sh-field' },
          el('span', { id: 'share-chapters-label' }, t('share.chapters')),
          component(SegmentedControl, {
            'aria-labelledby': 'share-chapters-label',
            options: [
              { value: 'all', label: t('share.all') },
              { value: 'some', label: t('share.some') },
            ],
            get value() {
              return range();
            },
            onChange: (value: string) => {
              setRange(value === 'some' ? 'some' : 'all');
              setPending(null);
            },
          }),
          when(
            () => range() === 'some',
            () => Range(shell, chapters, from, (at) => { setFrom(at); setPending(null); }, to, (at) => { setTo(at); setPending(null); }),
          ),
        ),
        el(
          'div',
          { class: 'sh-checkrow' },
          el('label', { for: 'share-comments' }, el('strong', {}, t('share.comments')), el('span', { class: 'sh-help sh-block', id: 'share-comments-help' }, t('share.commentsHelp'))),
          component(Checkbox, {
            id: 'share-comments',
            get checked() {
              return comments();
            },
            'aria-describedby': 'share-comments-help',
            onChange: (on: boolean) => {
              setComments(on);
              setPending(null);
            },
          }),
        ),
        el(
          'div',
          { class: 'sh-field' },
          el('label', { for: 'share-ends' }, t('share.ends'), ' ', el('span', { class: 'sh-help', id: 'share-ends-help' }, t('share.endsHelp'))),
          // A date: TextField's own input, which takes any type.
          component(TextInput, {
            id: 'share-ends',
            type: 'date',
            class: 'share-ends',
            min: today,
            'aria-describedby': 'share-ends-help share-error',
            get value() {
              return ends();
            },
            onChange: (value: string) => {
              setEnds(value);
              setPending(null);
            },
          }),
        ),
        component(Banner, { tone: 'neutral', icon: 'link', class: 'sh-note-box', children: t('share.keyNote') }),
      ),
    ),
    el('p', { id: 'share-error', class: 'ac-error c-modal__error', role: 'status' }, error),
    ModalButtons(
      component(Button, { class: 'share-cancel', onClick: () => ui.setDialog(null), children: t('share.cancel') }),
      component(Button, {
        variant: 'primary',
        type: 'submit',
        class: 'share-create',
        get loading() {
          return busy();
        },
        get children() {
          return busy() ? t('share.creating') : pending() === null ? t('share.create') : t('share.retry');
        },
      }),
    ),
  );
}

/** Decision 63: an account not paid is told the links come with the purchase. */
function Purchase(shell: Shell): Child {
  const { t, ui } = shell;
  return [
    ModalBody({}, el('p', { class: 'share-purchase' }, t('share.purchase'))),
    ModalButtons(component(Button, { variant: 'primary', class: 'share-done', onClick: () => ui.setDialog(null), children: t('share.close') })),
  ];
}

/** The Share dialog's contents: its head, then the form or the purchase, then the ready link. */
export function ShareDialog(shell: Shell): Child {
  const { t, sharing } = shell;
  const [made, setMade] = createSignal<Made | null>(null);
  return [
    Head(shell, () => (made() === null ? t('share.title') : t('share.readyTitle'))),
    // Part 41a deep-fix r1: the standing in a `when` of its own, as `whenSome` makes its fallback once.
    whenSome(made, (link) => Ready(shell, link()), () => when(() => sharing.paid(), () => Form(shell, (link) => {
      setMade(link);
    }), () => Purchase(shell))),
  ];
}
